/**
 * OutputRenderer - Renders a single RichOutput based on its type
 *
 * Handles multiple output types from code execution:
 * - PNG images (matplotlib charts)
 * - JPEG images
 * - SVG graphics
 * - HTML content (pandas DataFrames)
 * - Markdown text
 * - JSON data
 * - Plain text (fallback)
 *
 * Security: All HTML content is sanitized with DOMPurify before rendering
 * to prevent XSS attacks from malicious code output.
 */

import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { RichOutput } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface OutputRendererProps {
  /** The rich output to render */
  output: RichOutput;
  /** Callback when user clicks export button */
  onExport?: (type: 'png' | 'csv', data: string) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert an HTML table element to CSV format
 *
 * Extracts all rows from a table and converts to comma-separated values.
 * Handles escaping of quotes and commas in cell content.
 *
 * @param table - The HTML table element to convert
 * @returns CSV string representation of the table
 */
function tableToCSV(table: HTMLTableElement): string {
  // Get all rows from the table
  const rows = Array.from(table.querySelectorAll('tr'));

  // Convert each row to a CSV line
  return rows
    .map((row) => {
      // Get all cells (th or td) in the row
      const cells = Array.from(row.querySelectorAll('th, td'));

      // Convert each cell to a CSV value
      return cells
        .map((cell) => {
          let text = cell.textContent || '';

          // Escape quotes by doubling them, and wrap in quotes if contains comma/quote
          if (text.includes(',') || text.includes('"')) {
            text = `"${text.replace(/"/g, '""')}"`;
          }

          return text;
        })
        .join(',');
    })
    .join('\n');
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * OutputRenderer Component
 *
 * Renders a single RichOutput based on its type. The component automatically
 * detects the output type and renders the appropriate UI.
 *
 * Priority order (first match wins):
 * 1. PNG image (matplotlib charts)
 * 2. JPEG image
 * 3. SVG graphics
 * 4. HTML content (pandas DataFrames)
 * 5. Markdown text
 * 6. JSON data
 * 7. Plain text (fallback)
 */
export function OutputRenderer({ output, onExport }: OutputRendererProps) {
  // ==========================================================================
  // SANITIZE HTML CONTENT
  // ==========================================================================

  // Memoize the sanitized HTML to avoid re-sanitizing on every render
  // DOMPurify.sanitize strips dangerous tags/attributes while keeping safe formatting
  const sanitizedHtml = useMemo(() => {
    if (output.html) {
      return DOMPurify.sanitize(output.html, {
        USE_PROFILES: { html: true }, // Use the HTML sanitization profile
        ADD_ATTR: ['target'], // Allow target="_blank" for links
      });
    }
    return null;
  }, [output.html]);

  // ==========================================================================
  // RENDER PNG IMAGE (matplotlib charts)
  // ==========================================================================

  if (output.png) {
    return (
      <div className="relative group">
        {/* The chart image */}
        <img
          src={`data:image/png;base64,${output.png}`}
          alt="Chart output"
          className="max-w-full rounded-lg border border-gray-200"
        />

        {/* Export button - appears on hover */}
        <button
          onClick={() => onExport?.('png', output.png!)}
          className="
            absolute top-2 right-2 px-2 py-1
            bg-white/90 text-xs font-medium text-gray-700
            rounded shadow-sm border border-gray-200
            opacity-0 group-hover:opacity-100 transition-opacity
            hover:bg-gray-50
          "
        >
          Download PNG
        </button>
      </div>
    );
  }

  // ==========================================================================
  // RENDER JPEG IMAGE
  // ==========================================================================

  if (output.jpeg) {
    return (
      <div className="relative group">
        {/* The image */}
        <img
          src={`data:image/jpeg;base64,${output.jpeg}`}
          alt="Image output"
          className="max-w-full rounded-lg border border-gray-200"
        />

        {/* Export button - appears on hover */}
        <button
          onClick={() => onExport?.('png', output.jpeg!)}
          className="
            absolute top-2 right-2 px-2 py-1
            bg-white/90 text-xs font-medium text-gray-700
            rounded shadow-sm border border-gray-200
            opacity-0 group-hover:opacity-100 transition-opacity
            hover:bg-gray-50
          "
        >
          Download
        </button>
      </div>
    );
  }

  // ==========================================================================
  // RENDER SVG CONTENT
  // ==========================================================================

  if (output.svg) {
    return (
      <div
        className="max-w-full overflow-auto rounded-lg border border-gray-200 p-2 bg-white"
        // SVG is also sanitized for safety
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(output.svg) }}
      />
    );
  }

  // ==========================================================================
  // RENDER ARTIFACT FROM ISOLATED ORIGIN (secure, recommended)
  // ==========================================================================

  // If we have an artifact URL, render from isolated origin
  // This is the most secure option - served with strict CSP from R2
  if (output.artifactUrl) {
    return (
      <div className="relative group">
        {/* Open in new tab button */}
        <button
          onClick={() => window.open(output.artifactUrl, '_blank')}
          className="
            absolute top-2 right-2 z-10
            flex items-center gap-1.5 px-3 py-1.5
            bg-emerald-500 text-white text-xs font-semibold
            rounded-lg shadow-md
            hover:bg-emerald-600 transition-colors
          "
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Play Fullscreen
        </button>
        <iframe
          src={output.artifactUrl}
          sandbox="allow-scripts"
          className="w-full h-80 rounded-lg border border-gray-200 bg-white"
          title="Interactive output"
          referrerPolicy="no-referrer"
        />
        <p className="mt-1 text-[10px] text-gray-400 text-center">
          Interactive content (isolated origin)
        </p>
      </div>
    );
  }

  // ==========================================================================
  // RENDER INTERACTIVE HTML (fallback for inline HTML with scripts)
  // ==========================================================================

  // If HTML contains scripts or is a full document, render in sandboxed iframe
  // This is a fallback if artifact storage isn't available
  if (output.html && (output.html.includes('<script') || output.html.includes('<!DOCTYPE'))) {
    const blob = new Blob([output.html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    return (
      <div className="relative group">
        {/* Open in new tab button */}
        <button
          onClick={() => window.open(blobUrl, '_blank')}
          className="
            absolute top-2 right-2 z-10
            flex items-center gap-1.5 px-3 py-1.5
            bg-emerald-500 text-white text-xs font-semibold
            rounded-lg shadow-md
            hover:bg-emerald-600 transition-colors
          "
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Play Fullscreen
        </button>
        <iframe
          src={blobUrl}
          sandbox="allow-scripts"
          className="w-full h-80 rounded-lg border border-gray-200 bg-white"
          title="Interactive output"
        />
        <p className="mt-1 text-[10px] text-gray-400 text-center">
          Interactive content (sandboxed)
        </p>
      </div>
    );
  }

  // ==========================================================================
  // RENDER HTML CONTENT (pandas DataFrames, static HTML)
  // ==========================================================================

  if (sanitizedHtml) {
    return (
      <div className="relative group">
        {/* The table container with scrolling */}
        <div
          className="
            overflow-auto max-h-80 rounded-lg border border-gray-200
            [&_table]:w-full [&_table]:text-sm
            [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:border-b
            [&_td]:px-3 [&_td]:py-2 [&_td]:border-b [&_td]:border-gray-100
            [&_tr:hover]:bg-gray-50
          "
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />

        {/* Export as CSV button - appears on hover */}
        <button
          onClick={() => {
            // Parse the sanitized HTML to extract table data
            const parser = new DOMParser();
            const doc = parser.parseFromString(sanitizedHtml, 'text/html');
            const table = doc.querySelector('table');

            if (table) {
              const csv = tableToCSV(table);
              onExport?.('csv', csv);
            }
          }}
          className="
            absolute top-2 right-2 px-2 py-1
            bg-white/90 text-xs font-medium text-gray-700
            rounded shadow-sm border border-gray-200
            opacity-0 group-hover:opacity-100 transition-opacity
            hover:bg-gray-50
          "
        >
          Download CSV
        </button>
      </div>
    );
  }

  // ==========================================================================
  // RENDER MARKDOWN CONTENT
  // ==========================================================================

  if (output.markdown) {
    // For now, just show as preformatted text
    // Could integrate a markdown renderer (react-markdown) later
    return (
      <div className="prose prose-sm max-w-none p-3 bg-gray-50 rounded-lg">
        <pre className="whitespace-pre-wrap text-sm">{output.markdown}</pre>
      </div>
    );
  }

  // ==========================================================================
  // RENDER JSON CONTENT
  // ==========================================================================

  if (output.json) {
    return (
      <pre className="p-3 bg-gray-50 rounded-lg text-sm overflow-auto max-h-60">
        {JSON.stringify(output.json, null, 2)}
      </pre>
    );
  }

  // ==========================================================================
  // RENDER PLAIN TEXT (fallback)
  // ==========================================================================

  if (output.text) {
    return (
      <pre className="p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap">
        {output.text}
      </pre>
    );
  }

  // No renderable content
  return null;
}
